import fs from 'node:fs/promises'
import os from 'node:os'
import path from 'node:path'
import { z } from 'zod'

const SCALAR_METADATA_FILE_NAME = '.scalar-config'
export const METADATA_FILE_PATH = path.join(
  os.homedir(),
  SCALAR_METADATA_FILE_NAME,
)

export const authSchema = z.union([
  z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
  }),
  z.object({
    accessToken: z.string(),
    personalToken: z.string(),
  }),
])

export type Auth = z.infer<typeof authSchema>

const metadataSchema = z.object({
  lastUpdateCheck: z.number(),
  lastKnownVersion: z.string(),
  auth: authSchema.nullable().default(null),
})

export type Metadata = z.infer<typeof metadataSchema>

/**
 * Updates the metadata file with new values while preserving existing data
 * @param metadata - Partial metadata object containing fields to update
 * @example
 * // Update last update check timestamp
 * await setMetadata({ lastUpdateCheck: Date.now() })
 * // Update multiple fields
 * await setMetadata({
 *   lastUpdateCheck: Date.now(),
 *   someOtherField: 'value'
 * })
 */
export const setMetadata = async (
  metadata: Partial<z.infer<typeof metadataSchema>>,
) => {
  const currentMetadata = await getMetadata()
  await fs.writeFile(
    METADATA_FILE_PATH,
    JSON.stringify({ ...currentMetadata, ...metadata }),
  )
}

/**
 * Checks if a file exists at the given path
 * @param path - The file path to check
 * @returns Promise resolving to true if file exists, false otherwise
 * @example
 * // Check if config file exists
 * const exists = await fileExists('./config.json')
 * if (exists) {
 *   // File exists, do something
 * }
 */
const fileExists = async (path: string) =>
  !!(await fs.stat(path).catch(() => false))

/**
 * Reads and parses the metadata file, returning an empty object if the file doesn't exist
 * @returns Promise resolving to the parsed metadata object
 * @example
 * // Read metadata file
 * const metadata = await getMetadata()
 * // Access last update check time
 * const lastCheck = metadata.lastUpdateCheck
 */
export const getMetadata = async () => {
  const metadata = (await fileExists(METADATA_FILE_PATH))
    ? await fs.readFile(METADATA_FILE_PATH, { encoding: 'utf-8' })
    : '{}'

  try {
    return metadataSchema.partial().parse(JSON.parse(metadata))
  } catch {
    return {}
  }
}
